import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { getContentAttachments, getQuickAccessFolder, getRecentContentAttachments } from "../../../../shared/services/contentManger.services";
import "./TableFileUploadModal.scss";
import { icon, label } from "../../../../config";
import { convertDateFormat, formatBytes } from "../../../ContentManagementSystem/helper";
import { TABLE_FILE_UPLOAD } from "../../FormsConstant";

const { TABS, FILE_SOURCE, COLUMN_HEADERS, LOADING_TEXT, EMPTY_TEXT, DEVICE_HINT_TEXT, CHOOSE_FILE_BTN, CLOSE_ARIA_LABEL, FOLDER_TYPE } = TABLE_FILE_UPLOAD;

const FileRow = ({ file, onClick }) => (
  <tr onClick={() => onClick(file)}>
    <td>
      <div className="table-file-upload-modal__file-icon">
       <span>{icon.FORM_ICON}</span>
        <span className="table-file-upload-modal__file-name">{file.name}</span>
      </div>
    </td>
    <td>{convertDateFormat(file?.createdAt)}</td>
    <td>{file?.size ? formatBytes(file?.size) : ""}</td>
  </tr>
);

const TableFileUploadModal = ({ onSelect, onClose, multiple = false, acceptedFileTypes = "", maxFileSize = null  }) => {
  const [activeTab, setActiveTab] = useState(TABS.QUICK_ACCESS);
  const [quickAccessFiles, setQuickAccessFiles] = useState([]);
  const [recentFiles, setRecentFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRemove, setAutoRemove] = useState(false);
  const fileInputRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const params = useParams();
  const projectId = params?.projectId ?? null;

  const loadQuickAccessFiles = useCallback(async () => {
    setLoading(true);
    try {
      const folderData = await getQuickAccessFolder();
      if (folderData) {
        const result = await getContentAttachments({
          parentId: folderData._id ?? null,
          userId: user?.id,
        });
        const items = result?.data ?? result ?? [];
        setQuickAccessFiles(Array.isArray(items) ? items.filter((f) => f.type !== FOLDER_TYPE) : []);
      }
    } catch {
      setQuickAccessFiles([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, user?.id]);

  const loadRecentFiles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRecentContentAttachments({ userId: user?.id });
      const items = result?.data ?? result ?? [];
      setRecentFiles(Array.isArray(items) ? items.filter((f) => f.type !== FOLDER_TYPE) : []);
    } catch {
      setRecentFiles([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, user?.id]);

  useEffect(() => {
    loadQuickAccessFiles();
  }, [loadQuickAccessFiles]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === TABS.RECENT && recentFiles.length === 0) {
      loadRecentFiles();
    }
  };

  const handleCmsFileSelect = (file) => {
    onSelect({
      name: file.name,
      size: file.size ?? 0,
      attachmentKey: file.attachmentKey ?? "",
      id: file._id,
      dateCreated: file.createdAt,
      source: FILE_SOURCE.CMS,
    }, autoRemove);
    if (autoRemove) {
      if (activeTab === TABS.QUICK_ACCESS) {
        setQuickAccessFiles((prev) => prev.filter((f) => f.id !== file.id));
      } else {
        setRecentFiles((prev) => prev.filter((f) => f.id !== file.id));
      }
    }
    onClose();
  };

  const handleDeviceFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files ?? []);
    if (!selectedFiles.length) return;

    const allowedExts = acceptedFileTypes
      ? acceptedFileTypes.split(",").map((ext) => ext.trim().toLowerCase().replace(/^\./, ""))
      : [];

    const entries = selectedFiles.reduce((acc, file) => {
      const ext = file.name.split(".").pop().toLowerCase();
      if (allowedExts.length && !allowedExts.includes(ext)) return acc;
      if (maxFileSize && file.size > maxFileSize) return acc;
      acc.push({
        name: file.name,
        size: file.size,
        rawFile: file,
        uid: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        source: FILE_SOURCE.DEVICE,
      });
      return acc;
    }, []);

    if (entries.length) onSelect( multiple ? entries : entries[0]);
    onClose();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const renderFileList = (files) => {
    if (loading) {
      return (
        <div className="table-file-upload-modal__loading">
          <span>{LOADING_TEXT}</span>
        </div>
      );
    }
    if (!files.length) {
      return <div className="table-file-upload-modal__empty">{EMPTY_TEXT}</div>;
    }
    return (
      <table className="table-file-upload-modal__file-table">
        <thead>
          <tr>
            <th>{COLUMN_HEADERS.NAME}</th>
            <th>{COLUMN_HEADERS.DATE_CREATED}</th>
            <th>{COLUMN_HEADERS.SIZE}</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <FileRow key={file.id} file={file} onClick={handleCmsFileSelect} />
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className="table-file-upload-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="table-file-upload-modal">
        {/* Tab header */}
        <div className="table-file-upload-modal__header">
          <div className="table-file-upload-modal__tabs">
            {Object.values(TABS).map((tab) => (
              <button
                key={tab}
                className={`table-file-upload-modal__tab${activeTab === tab ? " table-file-upload-modal__tab--active" : ""}`}
                onClick={() => handleTabChange(tab)}>
                {tab}
              </button>
            ))}
          </div>
          <button className="table-file-upload-modal__close" onClick={onClose} aria-label={CLOSE_ARIA_LABEL}>
            {icon.CLOSE}
          </button>
        </div>

        {/* Body */}
        <div className="table-file-upload-modal__body">
          {activeTab === TABS.QUICK_ACCESS && renderFileList(quickAccessFiles)}
          {activeTab === TABS.RECENT && renderFileList(recentFiles)}
          {activeTab === TABS.UPLOAD_VIA_DEVICE && (
            <div className="table-file-upload-modal__device-upload">
              <div className="table-file-upload-modal__device-icon">
                {icon.UPLOAD}
              </div>
              <p className="table-file-upload-modal__device-text">
                {DEVICE_HINT_TEXT}
              </p>
              <button
                className="table-file-upload-modal__device-btn"
                onClick={() => fileInputRef.current?.click()}>
                {CHOOSE_FILE_BTN}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                multiple={multiple}
                accept={acceptedFileTypes || undefined}
                onChange={handleDeviceFileChange}
              />
            </div>
          )}
        </div>

        {/* Footer — shown only on Quick Access and Recent */}
        {(activeTab === TABS.QUICK_ACCESS || activeTab === TABS.RECENT) && (
          <div className="table-file-upload-modal__footer">
            <span className="table-file-upload-modal__footer-info" title="When enabled, the file is removed from the list after you select it">
              &#x24D8;
            </span>
            <span>{label.AUTO_REMOVE}</span>
            <input
              type="checkbox"
              className="table-file-upload-modal__footer-checkbox"
              checked={autoRemove}
              onChange={(e) => setAutoRemove(e.target.checked)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TableFileUploadModal;
